/** The reusable delivery-log overview: a dense read-only summary of one
 *  finalized event delivery — identity/timing, denormalized context, the
 *  per-notification × method result rows, the orphan-recovery link, and the
 *  redelivery chain. Owns its
 *  fetch/loading/error so it works both as the /delivery-logs/:id Overview tab
 *  and as the grid's master-detail panel. The heavier S3-archive sections live
 *  on the detail page's Delivery/Debug tabs (lazy).
 *
 *  Every reference is a starting point (deliveryLogLinks): the participant IS
 *  the console booking, the order ref opens the order, the notifications open
 *  their notification, and event / log id / status / timestamps search the
 *  grid for siblings.
 */
import { Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as edlApi from '@/api/eventDeliveryLogs';
import type { DeliveryLogChainItem, DeliveryLogNotification } from '@/api/eventDeliveryLogs';
import { Field, FieldGrid, LinkField, SectionCard } from '@/components/ReadView';
import { act, utcTitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { READONLY_GRID_PROPS, READONLY_GRID_SX, Ts } from '@/features/bookings/overview/common';
import { formatDate } from '@/utils/format';

import { DeliveryStatusChip, MethodStatusChip, STATUS_ORPHANED } from '../deliveryLogsColumns';
import { capturedCount } from '../deliveryStatus';
import {
  chainItemActions,
  customIdActions,
  departureActions,
  emittedActions,
  errorSeriesActions,
  eventActions,
  finalizedActions,
  logIdActions,
  logTagActions,
  notificationActions,
  orderActions,
  participantActions,
  recipientActions,
  refActions,
  requestIdActions,
  statusActions,
  tenantDeliveryActions,
  tourActions,
  type DeliveryLogCtx,
} from './deliveryLogLinks';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function buildNotificationColumns(
  d: edlApi.DeliveryLogDetail,
  ctx: DeliveryLogCtx,
): GridColDef<DeliveryLogNotification>[] {
  return [
    {
      field: 'notification_name',
      headerName: 'Notification',
      flex: 1.4,
      minWidth: 200,
      renderCell: (p) => (
        <ValueActions actions={notificationActions(p.row.notification_id, d, ctx)}>
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography component="span" variant="body2" noWrap>
              <Link component={RouterLink} to={`/notifications/${p.row.notification_id}`} underline="hover">
                {p.row.notification_name || `#${p.row.notification_id}`}
              </Link>
            </Typography>
            <Typography component="span" variant="caption" color="text.secondary">
              #{p.row.notification_id}
            </Typography>
          </Stack>
        </ValueActions>
      ),
    },
    { field: 'delivery_method_name', headerName: 'Method', width: 130 },
    {
      field: 'status',
      headerName: 'Status',
      // Fits "Partially sent (allowlisted)" with its mark.
      width: 200,
      renderCell: (p) => <MethodStatusChip status={p.row.status} label={p.row.status_display} />,
    },
    { field: 'n_success', headerName: 'OK', width: 70, align: 'right', headerAlign: 'right' },
    { field: 'n_error', headerName: 'Errors', width: 80, align: 'right', headerAlign: 'right' },
    {
      field: 'error_series_id',
      headerName: 'Series',
      width: 110,
      renderCell: (p) =>
        p.row.error_series_id ? (
          <ValueActions actions={errorSeriesActions(p.row.error_series_id, ctx)}>
            <Box component="span" sx={mono}>{p.row.error_series_id}</Box>
          </ValueActions>
        ) : (
          '—'
        ),
    },
  ];
}

/** The orphan-recovery story of this row, in both directions.
 *
 *  An ORPHANED delivery (status 9) is one whose worker died mid-flight; the
 *  recovery cron examines it and either re-dispatches what it never attempted
 *  (status 10, `recovery_uuid` → the delivery that finished the job) or records
 *  why it couldn't (11/12). `recovered_from_uuid` is the inverse link, on the
 *  recovery itself. `tags.recovery` carries the reasoning.
 */
function RecoveryBlock({ d }: { d: edlApi.DeliveryLogDetail }) {
  const info = (d.tags?.recovery ?? null) as Record<string, unknown> | null;
  if (!d.recovery_uuid && !d.recovered_from_uuid && !info) {
    return (
      <Typography variant="body2" color="text.disabled">
        {d.status === STATUS_ORPHANED
          ? 'Not examined yet — the recovery cron transitions every orphan within ~5 minutes.'
          : 'Not an orphaned delivery.'}
      </Typography>
    );
  }
  const notifications = (info?.notifications ?? []) as { id?: number; name?: string; methods?: number[] }[];
  const skipped = (info?.skipped ?? []) as { id?: number; reason?: string; methods?: number[] }[];
  const notificationRef = (n: { id?: number; name?: string }) =>
    n.id ? (
      <ValueActions actions={[act.open(`/notifications/${n.id}`, 'Open notification')]}>
        [{n.id}] {n.name ?? ''}
      </ValueActions>
    ) : (
      <>[?] {n.name ?? ''}</>
    );
  return (
    <Box>
      <FieldGrid>
        <LinkField
          label="Recovered by"
          to={d.recovery_uuid ? `/delivery-logs/${d.recovery_uuid}` : null}
          text={d.recovery_uuid ? <Box component="span" sx={mono}>{d.recovery_uuid}</Box> : null}
          {...(d.recovery_uuid && { actions: chainItemActions(d.recovery_uuid) })}
        />
        <LinkField
          label="Recovery of"
          to={d.recovered_from_uuid ? `/delivery-logs/${d.recovered_from_uuid}` : null}
          text={
            d.recovered_from_uuid ? <Box component="span" sx={mono}>{d.recovered_from_uuid}</Box> : null
          }
          {...(d.recovered_from_uuid && { actions: chainItemActions(d.recovered_from_uuid) })}
        />
        <Field label="Outcome" value={(info?.outcome as string) ?? '—'} />
        <Field label="Reason" value={(info?.reason as string) ?? '—'} />
        <Field label="Examined" value={<Ts value={(info?.ts as string) ?? null} />} />
        <Field label="Requested by" value={(info?.source as string) ?? '—'} />
      </FieldGrid>
      {notifications.length > 0 && (
        <Typography variant="body2" sx={{ mt: 1 }} component="div">
          Re-dispatched:{' '}
          {notifications.map((n, i) => (
            <Box component="span" key={n.id ?? i}>
              {i > 0 && '; '}
              {notificationRef(n)} (methods {(n.methods ?? []).join(', ')})
            </Box>
          ))}
        </Typography>
      )}
      {skipped.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} component="div">
          Skipped:{' '}
          {skipped.map((sk, i) => (
            <Box component="span" key={sk.id ?? i}>
              {i > 0 && '; '}
              {notificationRef({ id: sk.id, name: sk.reason })}
            </Box>
          ))}
        </Typography>
      )}
    </Box>
  );
}

function ChainList({ chain, ctx }: { chain: DeliveryLogChainItem[]; ctx: DeliveryLogCtx }) {
  if (chain.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        No other deliveries share this log ID.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {chain.map((c) => (
        <Stack key={c.id} direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <ValueActions actions={chainItemActions(c.id)} title={utcTitle(c.created_ts)}>
            <Link component={RouterLink} to={`/delivery-logs/${c.id}`} underline="hover" variant="body2">
              {formatDate(c.created_ts)}
            </Link>
          </ValueActions>
          <ValueActions chip actions={statusActions(c.status_display, ctx)}>
            <DeliveryStatusChip status={c.status} label={c.status_display} />
          </ValueActions>
          {c.log_tag && (
            <ValueActions actions={logTagActions(c.log_tag, ctx)}>
              <Typography component="span" variant="caption" color="text.secondary">
                {c.log_tag}
              </Typography>
            </ValueActions>
          )}
          {c.error_series_id && (
            <ValueActions actions={errorSeriesActions(c.error_series_id, ctx)}>
              <Typography component="span" variant="caption" sx={mono}>
                {c.error_series_id}
              </Typography>
            </ValueActions>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

/** Flexible ad-hoc `tags` JSONB as label:value lines. */
function TagsBlock({ tags }: { tags: Record<string, unknown> | null }) {
  const entries = Object.entries(tags ?? {});
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        No tags recorded.
      </Typography>
    );
  }
  return (
    <FieldGrid>
      {entries.map(([k, v]) => {
        const text = v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
        return (
          <Field
            key={k}
            label={k}
            value={text}
            {...(v != null && { actions: [act.copy(text, `Copy ${k}`)] })}
          />
        );
      })}
    </FieldGrid>
  );
}

export function DeliveryLogSummary({
  logId,
  showHeader = true,
}: {
  logId: string;
  /** Hidden on the detail page (PageHeader shows identity); shown in the grid panel. */
  showHeader?: boolean;
}) {
  const { data: d, isLoading, error } = useQuery({
    queryKey: ['delivery-logs', 'detail', logId],
    queryFn: () => edlApi.retrieve(logId),
    enabled: Boolean(logId),
  });
  const ctx = useMemo<DeliveryLogCtx>(() => ({ tenantId: d?.tour_operator_id }), [d?.tour_operator_id]);
  const notificationColumns = useMemo(
    () => (d ? buildNotificationColumns(d, ctx) : []),
    [d, ctx],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !d) return <Alert severity="error">Couldn’t load delivery log {logId}.</Alert>;

  const tenant = d.tour_operator_id != null ? { id: d.tour_operator_id } : null;

  return (
    <Box>
      {showHeader && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mr: 1, ...mono }}>
            <ValueActions actions={eventActions(d.event, d, ctx)}>{d.event}</ValueActions>
          </Typography>
          <ValueActions chip actions={statusActions(d.status_display, ctx)}>
            <DeliveryStatusChip status={d.status} label={d.status_display} />
          </ValueActions>
          <ValueActions chip actions={logIdActions(d.log_id, ctx)}>
            <Chip size="small" variant="outlined" label={d.log_id} sx={mono} />
          </ValueActions>
          {tenant && (
            <ValueActions actions={tenantDeliveryActions(tenant)}>
              <Link component={RouterLink} to={`/tour-operators/${tenant.id}`} underline="hover" variant="body2">
                {d.tour_operator_code ?? `#${tenant.id}`}
              </Link>
            </ValueActions>
          )}
        </Stack>
      )}

      <SectionCard title="Identity & timing">
        <FieldGrid>
          <Field
            label="UUID"
            value={<Box component="span" sx={mono}>{d.id}</Box>}
            actions={[act.copy(d.id, 'Copy uuid')]}
          />
          <Field
            label="Event"
            value={<Box component="span" sx={mono}>{d.event}</Box>}
            actions={eventActions(d.event, d, ctx)}
          />
          <Field
            label="Log ID"
            value={<Box component="span" sx={mono}>{d.log_id}</Box>}
            actions={logIdActions(d.log_id, ctx)}
          />
          <Field
            label="Log tag"
            value={d.log_tag ?? '—'}
            {...(d.log_tag && { actions: logTagActions(d.log_tag, ctx) })}
          />
          <Field
            label="Status"
            value={<DeliveryStatusChip status={d.status} label={d.status_display} />}
            actions={statusActions(d.status_display, ctx)}
          />
          <Field label="Rendering language" value={d.rendering_lang ?? '—'} />
          <Field
            label="Emitted"
            value={<Ts value={d.created_ts} />}
            actions={emittedActions(d.created_ts, ctx)}
          />
          <Field
            label="Finalized"
            value={<Ts value={d.finalized_ts} />}
            {...(d.finalized_ts && { actions: finalizedActions(d.finalized_ts, ctx) })}
          />
          <Field label="Expires" value={<Ts value={d.expires_ts} />} />
          <Field label="Duration" value={`${d.duration_ms} ms`} />
          <LinkField
            label="Previous delivery"
            to={d.previous_eda_uuid ? `/delivery-logs/${d.previous_eda_uuid}` : null}
            text={d.previous_eda_uuid ? <Box component="span" sx={mono}>{d.previous_eda_uuid}</Box> : null}
            {...(d.previous_eda_uuid && { actions: chainItemActions(d.previous_eda_uuid) })}
          />
          <Field
            label="Error series"
            value={d.error_series_id ? <Box component="span" sx={mono}>{d.error_series_id}</Box> : '—'}
            {...(d.error_series_id && { actions: errorSeriesActions(d.error_series_id, ctx) })}
          />
        </FieldGrid>
      </SectionCard>

      <SectionCard title="Context">
        <FieldGrid>
          <LinkField
            label="Tenant"
            to={tenant ? `/tour-operators/${tenant.id}` : null}
            text={d.tour_operator_code ?? (tenant ? `#${tenant.id}` : null)}
            {...(tenant && { actions: tenantDeliveryActions(tenant) })}
          />
          <LinkField
            label="Recipient"
            to={d.user_id != null ? `/users/${d.user_id}` : null}
            text={d.user_email ?? (d.user_id != null ? `#${d.user_id}` : null)}
            {...((d.user_id != null || d.user_email) && { actions: recipientActions(d, ctx) })}
          />
          <LinkField
            label="Tour"
            to={d.tour_id != null ? `/tours/${d.tour_id}` : null}
            text={d.tour_id != null ? `#${d.tour_id}` : null}
            {...(d.tour_id != null && { actions: tourActions({ id: d.tour_id }, ctx) })}
          />
          {/* participant_id IS the console booking pk (Group_Tour_User). */}
          <LinkField
            label="Participant"
            to={d.participant_id != null ? `/bookings/${d.participant_id}` : null}
            text={d.participant_id != null ? `Booking #${d.participant_id}` : null}
            {...(d.participant_id != null && { actions: participantActions(d.participant_id, ctx) })}
          />
          <Field label="Trip start" value={<Ts value={d.trip_start_ts} />} />
          <Field
            label="Departure"
            value={d.departure ?? (d.departure_id != null ? `#${d.departure_id}` : '—')}
            {...(d.departure && { actions: departureActions(d.departure, ctx) })}
          />
          <LinkField
            label="Order #"
            to={d.order_id != null ? `/orders/${d.order_id}` : null}
            text={d.order_ref ?? (d.order_id != null ? `#${d.order_id}` : null)}
            {...((d.order_id != null || d.order_ref) && {
              actions: orderActions({ order_id: d.order_id, order_ref: d.order_ref }, ctx),
            })}
          />
          {/* The booking-SYSTEM id (`booking=`), not the console pk above. */}
          <Field
            label="Booking ID"
            value={d.booking_id ?? '—'}
            {...(d.booking_id && { actions: customIdActions(d.booking_id, ctx) })}
          />
          <Field
            label="Supplier #"
            value={d.supplier_ref ?? '—'}
            {...(d.supplier_ref && { actions: refActions('supplier', d.supplier_ref, ctx) })}
          />
          <Field
            label="Reseller #"
            value={d.reseller_ref ?? '—'}
            {...(d.reseller_ref && { actions: refActions('reseller', d.reseller_ref, ctx) })}
          />
          <Field
            label="Request ID"
            value={d.request_id ? <Box component="span" sx={mono}>{d.request_id}</Box> : '—'}
            {...(d.request_id && { actions: requestIdActions(d.request_id, d.created_ts, ctx) })}
          />
          <Field
            label="Twilio conversation"
            value={d.twilio_conversation_id != null ? `#${d.twilio_conversation_id}` : '—'}
            {...(d.twilio_conversation_id != null && {
              actions: [act.copy(String(d.twilio_conversation_id), 'Copy conversation id')],
            })}
          />
        </FieldGrid>
      </SectionCard>

      <SectionCard title="Tags">
        <TagsBlock tags={d.tags} />
      </SectionCard>

      <SectionCard
        title={`Notification deliveries (${[
          `${d.n_notifications} — ${d.n_success} ok`,
          `${d.n_error} errors`,
          capturedCount(d.n_captured),
        ]
          .filter(Boolean)
          .join(', ')})`}
      >
        {d.notifications.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            No notification result rows recorded.
          </Typography>
        ) : (
          <DataGridPro<DeliveryLogNotification>
            {...READONLY_GRID_PROPS}
            sx={READONLY_GRID_SX}
            rows={d.notifications}
            columns={notificationColumns}
            getRowId={(r) => r.id}
          />
        )}
      </SectionCard>

      {Boolean(
        d.status === STATUS_ORPHANED
          || d.recovery_uuid
          || d.recovered_from_uuid
          || d.tags?.recovery,
      ) && (
        <SectionCard title="Orphan recovery">
          <RecoveryBlock d={d} />
        </SectionCard>
      )}

      <SectionCard title={`Redelivery chain (${d.chain.length})`}>
        <ChainList chain={d.chain} ctx={ctx} />
      </SectionCard>
    </Box>
  );
}
